<template>
  <div>
  <AppModal
    :show="true"
    @close="$emit('close')"
    :title="(props.document as any).fileName || props.document.originalName || props.document.name"
    size="large"
    :show-close="true"
    :no-padding="true"
  >
    <!-- Document info and preview -->
    <div class="document-viewer">
      <div class="document-viewer__info">
        <div class="document-viewer__file-title">
          <div v-html="getFileIcon((props.document as any).fileName || props.document.originalName || props.document.name)" class="document-viewer__file-icon"></div>
          <div class="document-viewer__file-details">
            <h4 class="document-viewer__file-name">{{ (props.document as any).fileName || props.document.originalName || props.document.name }}</h4>
            <div class="document-viewer__file-meta">
              <span v-if="(props.document as any).fileSize || props.document.size" class="document-viewer__file-size">{{ formatFileSize((props.document as any).fileSize || props.document.size) }}</span>
              <span class="document-viewer__file-type">{{ fileType.toUpperCase() }}</span>
            </div>
          </div>
        </div>
        
        <div class="document-viewer__info-grid">
          <div class="document-viewer__info-item">
            <strong>{{ $t('documents.documentType') }}:</strong> 
            {{ $t(`documents.types.${(props.document as any).docType || props.document.type}`) || (props.document as any).docType || props.document.type }}
          </div>
          <div class="document-viewer__info-item" v-if="(props.document as any).description">
            <strong>{{ $t('documents.description') }}:</strong> 
            {{ (props.document as any).description }}
          </div>
          <div class="document-viewer__info-item">
            <strong>{{ $t('documents.uploadDate') }}:</strong> 
            {{ formatDate((props.document as any).createdAt || props.document.uploadDate) }}
          </div>
        </div>
      </div>
        <FilePreview
        :fileName="(props.document as any).fileName || props.document.originalName || props.document.name"
        :fileUrl="documentUrl || ''"
        :fileType="fileType"
        :loading="loading"
        :error="error || ''"
        class="document-viewer__preview"
      >
        <template #error-actions>
          <div class="document-viewer__preview-actions">
            <AppButton variant="outline" size="small" @click="loadDocument">
              {{ $t('common.retry') }}
            </AppButton>
          </div>
        </template>
        <template #file-icon>
          <div v-html="getFileIcon((props.document as any).fileName || props.document.originalName || props.document.name)"></div>
        </template>
      </FilePreview>
    </div>
      <!-- Custom footer with actions -->
    <template #footer>
      <div class="document-viewer__actions">
        <AppButton 
          v-if="canAnnotate"
          type="button" 
          variant="success" 
          size="medium" 
          @click="openAnnotator"
          :disabled="loading"
        >
          <i class="fas fa-pencil-alt"></i>
          {{ $t('annotation.annotate') }}
        </AppButton>
        <AppButton 
          type="button" 
          variant="outline" 
          size="medium" 
          @click="downloadDocument"
          :disabled="loading"
        >
          <i class="fas fa-download"></i>
          {{ $t('documents.download') }}
        </AppButton>
        <AppButton 
          type="button" 
          variant="primary" 
          size="medium" 
          @click="$emit('close')"
        >
          {{ $t('common.close') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
  
  <!-- Annotation Modal -->
  <teleport to="body">
    <DocumentAnnotator
      v-if="showAnnotator"
      :projectId="props.projectId"
      :documentId="props.document.id"
      :documentUrl="documentUrl || ''"
      :fileName="(props.document as any).fileName || props.document.originalName || props.document.name"
      :fileType="fileType"
      @close="closeAnnotator"
      @saved="handleAnnotationSaved"
    />
  </teleport>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed } from 'vue';
// @ts-ignore
import { onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/composables/useStore';
import { usePermissions } from '@/composables/usePermissions';
import { PermissionType } from '@/services/permission.service';
import AppModal from '../../base/Modal.vue';
import AppButton from '../../base/AppButton.vue';
import FilePreview from './FilePreview.vue';
import DocumentAnnotator from './DocumentAnnotator.vue';
import DocumentService from '@/services/documents.service';
import type { Document } from '@/types/services';

interface Props {
  projectId: string | number;
  document: Document;
}

interface Emits {
  (e: 'close'): void;
}

const { t } = useI18n();
const store = useStore();
const { hasPermissionForProject } = usePermissions();

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<Emits>();

const loading = ref<boolean>(true);
const error = ref<string | null>(null);
const documentUrl = ref<string | null>(null);
const showAnnotator = ref<boolean>(false);

// Check file type to determine if we should try loading a preview
const fileType = computed<string>(() => {
  const fileName = (props.document as any).fileName || props.document.originalName || props.document.name || '';
  return fileName.split('.').pop()?.toLowerCase() || '';
});

const isPreviewable = computed(() => {
  const previewableTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'pdf', 'txt', 'svg'];
  return previewableTypes.includes(fileType.value);
});

// Format date
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString(store.getters.getCurrentLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format file size
const formatFileSize = (bytes: number | undefined): string => {
  if (!bytes || bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get file icon based on extension
const getFileIcon = (filename: string | undefined): string => {
  if (!filename) return '';

  const extension = filename.split('.').pop()?.toLowerCase();

  const iconMap: Record<string, string> = {
    'pdf': '<i class="fas fa-file-pdf" style="color: #e74c3c; font-size: 3rem;"></i>',
    'doc': '<i class="fas fa-file-word" style="color: #2980b9; font-size: 3rem;"></i>',
    'docx': '<i class="fas fa-file-word" style="color: #2980b9; font-size: 3rem;"></i>',
    'xls': '<i class="fas fa-file-excel" style="color: #27ae60; font-size: 3rem;"></i>',
    'xlsx': '<i class="fas fa-file-excel" style="color: #27ae60; font-size: 3rem;"></i>',
    'ppt': '<i class="fas fa-file-powerpoint" style="color: #e67e22; font-size: 3rem;"></i>',
    'pptx': '<i class="fas fa-file-powerpoint" style="color: #e67e22; font-size: 3rem;"></i>',
    'jpg': '<i class="fas fa-file-image" style="color: #3498db; font-size: 3rem;"></i>',
    'jpeg': '<i class="fas fa-file-image" style="color: #3498db; font-size: 3rem;"></i>',
    'png': '<i class="fas fa-file-image" style="color: #3498db; font-size: 3rem;"></i>',
    'gif': '<i class="fas fa-file-image" style="color: #3498db; font-size: 3rem;"></i>',
    'zip': '<i class="fas fa-file-archive" style="color: #8e44ad; font-size: 3rem;"></i>',
    'rar': '<i class="fas fa-file-archive" style="color: #8e44ad; font-size: 3rem;"></i>',
    'txt': '<i class="fas fa-file-alt" style="color: #7f8c8d; font-size: 3rem;"></i>',
    'mp3': '<i class="fas fa-file-audio" style="color: #f39c12; font-size: 3rem;"></i>',
    'mp4': '<i class="fas fa-file-video" style="color: #d35400; font-size: 3rem;"></i>',
  };

  return extension ? (iconMap[extension] || '<i class="fas fa-file" style="color: #95a5a6; font-size: 3rem;"></i>') : '';
};

// Load document
const loadDocument = async () => {
  if (!props.projectId || !props.document?.id) {
    error.value = t('errors.invalidDocumentView');
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Only try to load preview for files we can display in browser
    if (isPreviewable.value) {    // Get document data for viewing
      const result = await DocumentService.getDocumentForView(
        props.projectId, 
        props.document.id
      );
      
      // The data returned should be a Blob directly from our updated service
      if (result && result.data instanceof Blob) {
        documentUrl.value = URL.createObjectURL(result.data);
      } else {
        console.error('Invalid response format - expected a Blob object');
        error.value = t('errors.invalidResponseFormat');
      }
    }
    
    loading.value = false;
  } catch (err: any) {
    console.error('Error loading document:', err);
    error.value = err.message || t('errors.loadingDocumentError');
    loading.value = false;
  }
};

// Download the document
const downloadDocument = async (): Promise<void> => {
  try {
    await DocumentService.downloadDocument(
      props.projectId, 
      props.document.id, 
      (props.document as any).fileName || props.document.originalName || props.document.name
    );
  } catch (err: any) {
    console.error('Error downloading document:', err);
    store.dispatch('notifications/showError', t('errors.downloadingFileError'));
  }
};

// Check if user can annotate (requires UPLOAD_DOCUMENT permission)
const canAnnotate = computed(() => {
  const annotableTypes = ['jpg', 'jpeg', 'png', 'pdf', 'gif', 'webp', 'bmp'];
  return annotableTypes.includes(fileType.value) && 
         hasPermissionForProject(PermissionType.UPLOAD_DOCUMENT, props.projectId).value;
});

// Open annotator
const openAnnotator = () => {
  console.log('Opening annotator...', {
    documentUrl: documentUrl.value,
    projectId: props.projectId,
    documentId: props.document.id,
    fileType: fileType.value
  });
  
  if (!documentUrl.value) {
    console.error('Document URL is null - cannot open annotator');
    store.dispatch('notifications/showError', t('errors.loadingDocumentError'));
    return;
  }
  
  showAnnotator.value = true;
};

// Close annotator
const closeAnnotator = () => {
  console.log('Closing annotator...');
  showAnnotator.value = false;
};

// Handle annotation saved
const handleAnnotationSaved = () => {
  store.dispatch('notifications/showSuccess', t('annotation.savedSuccessfully'));
};

// Lifecycle hooks
onMounted(() => {
  loadDocument();
});

onUnmounted(() => {
  // Clean up blob URL if it exists
  if (documentUrl.value) {
    URL.revokeObjectURL(documentUrl.value);
  }
});
</script>

<style scoped>
/* ==========================================================================
   DOCUMENT VIEWER MODAL STYLES
   ========================================================================== */

.document-viewer {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  padding: var(--space-lg);
}

.document-viewer__info {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--border-light);
  flex-shrink: 0;
}

.document-viewer__file-title {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.document-viewer__file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.document-viewer__file-details {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  flex: 1;
  min-width: 0;
}

.document-viewer__file-name {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  word-break: break-word;
  line-height: 1.3;
}

.document-viewer__file-meta {
  display: flex;
  gap: var(--space-md);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

.document-viewer__file-size, 
.document-viewer__file-type {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  font-weight: var(--font-weight-medium);
}

.document-viewer__info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-sm);
}

.document-viewer__info-item {
  color: var(--text-primary);
  line-height: 1.5;
  font-size: var(--font-size-sm);
}

.document-viewer__info-item strong {
  font-weight: var(--font-weight-semibold);
  margin-right: var(--space-xs);
  color: var(--text-secondary);
}

.document-viewer__preview {
  width: 100%;
  min-height: 800px;
}

.document-viewer__preview-actions {
  margin-top: var(--space-md);
}

.document-viewer__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm);
  padding: var(--space-lg);
  background: var(--surface-secondary, #f8f9fa);
  border-top: 1px solid var(--border-light, #e9ecef);
  margin: 0 calc(-1 * var(--space-lg));
  margin-top: var(--space-lg);
}

/* ==========================================================================
   RESPONSIVE DESIGN
   ========================================================================== */

@media (max-width: 1024px) {
  .document-viewer__preview {
    min-height: 700px;
  }
}

@media (max-width: 768px) {
  .document-viewer {
    padding: var(--space-md);
  }
  
  .document-viewer__preview {
    min-height: 600px;
  }
  
  .document-viewer__info-grid {
    grid-template-columns: 1fr;
  }
  
  .document-viewer__file-title {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-sm);
  }
  
  .document-viewer__file-meta {
    flex-direction: column;
    gap: var(--space-xs);
  }
  
  .document-viewer__actions {
    flex-direction: column-reverse;
    gap: var(--space-xs);
    margin: 0 calc(-1 * var(--space-md));
    margin-top: var(--space-md);
    padding: var(--space-md);
  }
  
  .document-viewer__actions button {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .document-viewer {
    gap: var(--space-md);
    padding: var(--space-sm);
  }
  
  .document-viewer__preview {
    min-height: 500px;
  }
  
  .document-viewer__info {
    gap: var(--space-sm);
    padding-bottom: var(--space-sm);
  }
  
  .document-viewer__file-name {
    font-size: var(--font-size-md);
  }
  
  .document-viewer__actions {
    margin: 0 calc(-1 * var(--space-sm));
    margin-top: var(--space-sm);
    padding: var(--space-sm);
  }
}
</style>
